/**
 * typed-control.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output
} from "@angular/core";
import { AbstractControl, FormArray, FormControl, FormGroup }
  from "@angular/forms";

import { IntakeStep } from "../../algorithms/structure/intake-step";
import { SettingService } from "../../configuration/setting.service";
import { IntakeControlBuilder } from "../../intake-form/control.builder";
import { DataType } from "./data-types";
import { StepPresentationHints } from "./presentation-hints";
import { TypedData, TypedDataHolder } from "./typed-data";
import { CollaborationService } from "app/intake-form/collaboration.service";
import { IntakeStepService } from "app/algorithms/management/intake-step.service";
import { AlgorithmService } from "app/algorithms/management/algorithm.service";

/**
 * The {@link TypedControlComponent} forms a basis for all input fields that are
 * connected to a {@link TypedData} value. The biggest use case for this is the
 * coverage form's {@link IntakeStep}s, but it sees more usage over time.
 */
@Component({
  selector: "typed-control",
  template: `
  <template [ngIf]="!input?.hints?.specialPresentation">
    <template [ngIf]="input?.type">
      <div *ngIf="input.type !== 'boolean'" class="form-group">
        <div
          *ngIf="input.presentation"
          [innerHtml]="input.presentation"
          class="presentation"
          [class.renderSpacing]="input.hints?.inline">
        </div>

        <!-- *** RESTRICTED [any] *** -->
        <div *ngIf="input.restrictions?.length > 0">
          <!-- *** restricted SINGLE-[any] *** -->
          <div *ngIf="input.cardinality === 1" class="restricted">
            <typed-field
              [input]="input"
              [control]="control"
              [clearButton]="clearButton"
              [iconTray]="iconTray"
							[validationTray]="validationTray"
							[disableRestrictions]="disableRestrictions">
            </typed-field>
          </div>

          <!-- *** restricted MULTI-UNIQUE-[any] *** -->
          <div
            *ngIf="input.cardinality !== 1 && !input.allowsDuplicates"
            class="restricted multi">
            <typed-field
              [input]="input"
              [control]="control"
              [clearButton]="clearButton"
              [iconTray]="iconTray"
              [validationTray]="validationTray">
            </typed-field>
          </div>

        <!-- *** restricted MULTI-LIST-[any] *** -->
          <div
            *ngIf="input.cardinality !== 1 && input.allowsDuplicates"
            class="restricted multi">
            <div
              *ngFor="let field of control.controls; let i = index">
              <typed-field
                [input]="input"
                [control]="field"
                [index]="i"
                [clearButton]="clearButton"
                [iconTray]="iconTray"
                [validationTray]="validationTray">
              </typed-field>
            </div> <!-- /input group -->
          </div>
        </div>

        <!-- *** UNRESTRICTED [any] *** -->
        <div
          *ngIf="!(input.restrictions?.length > 0)"
          [ngSwitch]="input.type"
          [class]="input.type">

          <!-- JSON (DISPLAY ONLY atm) -->
          <template ngSwitchCase="json">
            <!-- <typed-field
                [input]="input"
                [control]="control"
                [clearButton]="false"
                [listActions]="false"
                [iconTray]="iconTray"
                [validationTray]="validationTray">
            </typed-field> -->
            <prettyjson [obj]="input.value"></prettyjson>
          </template>

          <!-- EMPTY -->
          <template ngSwitchCase="empty">
            <!-- no content! -->
          </template>

          <template ngSwitchDefault>
            <div *ngIf="input.cardinality === 1" class="scalar">
              <typed-field
                [input]="input"
                [control]="control"
                [clearButton]="clearButton"
                [iconTray]="iconTray"
                [validationTray]="validationTray">
              </typed-field>
            </div>
            <div
              *ngFor="let field of control.controls; let i = index"
              class="vector">
              <typed-field
                [input]="input"
                [control]="field"
                [index]="i"
                [listActions]="true"
                [clearButton]="clearButton"
                [iconTray]="iconTray"
                [validationTray]="validationTray">
              </typed-field>
            </div>
          </template>

        </div>
      </div> <!-- /form-group -->

      <table *ngIf="input.type === 'boolean' && !input?.hints?.useSubmitWithValue">
        <tbody><tr>
          <td class="presentation boolean-presentation" [innerHtml]="input?.presentation"></td>
          <td class="boolean">
            <!-- *** UNRESTRICTED BOOLEAN *** -->
            <!-- booleans are always unrestricted (and so far, arity of one) -->
            <typed-field
              *ngIf="input.cardinality === 1"
              [input]="input"
              [control]="control"
              [iconTray]="false"
              [clearButton]="false"
              [validationTray]="validationTray">
            </typed-field>

            <!-- initial hackneyed multi-boolean -->
            <div *ngIf="input.cardinality !== 1"> <!--input.cardinality !== 1-->
              <div
                *ngFor="let field of control.controls; let i = index">
                <typed-field
                  [input]="input"
                  [control]="field"
                  [index]="i"
                  [iconTray]="false"
                  listActions="remove"
                  [clearButton]="false"
                  [validationTray]="validationTray">
                </typed-field>
              </div>
            </div>
          </td>
        </tr></tbody>
      </table>

      <div *ngIf="input.type === 'boolean' && input?.hints?.useSubmitWithValue">
        <button (click)="manualSetAndChange(input?.hints?.useSubmitWithValue)" [innerHtml]="input?.presentation || 'Submit'" class="btn btn-success form-control booleanSubmit" [disabled]="input?.value == input?.hints?.useSubmitWithValue"></button>
      </div>

      <!-- cardinality control -->
      <div class="size-controls">
        <button *ngIf="input.cardinality === 0 && input.allowsDuplicates && input.hints.editable !== false"
          type="button"
          class="btn btn-success match-rwidth"
          (click)="addSubfield()">
          Add field...
        </button>
      </div>
    </template>
    <h4 *ngIf="!(input?.type)" class="bg-warning text-danger">
      Input has no type!
    </h4>
</template>
<div *ngIf="input?.hints?.specialPresentation === 'fcwi-income-table'" class="form-group special-income-section" (change)="changeCustom($event, 'fcwi-income-table')">
	<div><h4>About how much do you and the other people in your household make per month?</h4>
  <p>Enter the approximate amounts in this table. Note that many programs distinguish between <em>earned</em> and <em>unearned</em> income:</p>
  <ul>
    <li><em>Earned income</em> is generally what you make from "work activities" such as wages, salary, tips, long-term disability, employer reimbursements, and your net earnings from self-employment.</li>
    <li><em>Unearned income</em> is what you get from passive and not work sources, like social security, SSI, unemployment pay, retirement funds, gifts & inheritance, student financial aid, capital gains & loan interest, etc.</li>
  </ul>
  <p>If you aren't sure which category something falls into, take your best guess for now, and then be sure to mention it to the advocates or agents working with you.</p>
</div>
	<table class="table special-income-table">
		<tr>
      <th></th>
      <th>Earned (Work)</th>
      <th>Unearned (Non-work)</th>
    </tr>
    <tr>
        <th>You</th>
        <td> <input autocomplete="off" class="form-control" placeholder="Enter number." step="1" type="number" min="1" id="q-fcwi-estimated-earned-monthly-income"> </td>
        <td> <input autocomplete="off" class="form-control" placeholder="Enter number." step="1" type="number" min="1" id="q-fcwi-estimated-unearned-monthly-income"> </td>
    </tr>
    <!--<tr>
        <th>Your spouse</th>
        <td> <input autocomplete="off" class="form-control" placeholder="Enter number." step="1" type="number" min="1"> </td>
        <td> <input autocomplete="off" class="form-control" placeholder="Enter number." step="1" type="number" min="1"> </td>
    </tr>-->
    <tr>
        <th>Your dependents</th>
        <td> <input autocomplete="off" class="form-control" placeholder="Enter number." step="1" type="number" min="1" id="q-fcwi-estimated-earned-monthly-household" [disabled]="!cheaty_fcwiHouseholdEnabled()"> </td>
        <td> <input autocomplete="off" class="form-control" placeholder="Enter number." step="1" type="number" min="1" id="q-fcwi-estimated-unearned-monthly-household" [disabled]="!cheaty_fcwiHouseholdEnabled()"> </td>
    </tr>
  </table>
  </div>
`,
  styles: [
    `fieldset {
      position: relative;
    }
    .restricted.multi {
      margin-top: 15px;
      position: relative;
    }
    .minor-instruction {
      display: block;
      position: absolute;
      right: 0px;
      top: -15px;
    }
    .restricted select {
      overflow-y: auto;
    }
    .match-rwidth {
      width: 15%;
      min-width: 175px;
    }
    .btn-success.match-rwidth {
      /* TODO: make this semantic (add_subfield class?) */
      /*float: right;*/
    }
    .size-controls {
      width: 100%;
      text-align: right;
    }
    .vector+.vector {
      margin-top: 10px;
    }
    table, tr {
      width: 100%;
    }
    td.presentation {
      vertical-align: middle;
    }
    td.boolean {
      width: 180px;
      text-align: right;
      padding-bottom: 10px;
    }
    .renderSpacing {
      white-space: pre-wrap;
    }
    .booleanSubmit {
      margin-bottom: 1em;
    }
    .special-income-section {
      opacity: 90%;
    }`
  ]
})
export class TypedControlComponent implements OnInit
{
  /**
   * The data holder itself, along with its type information
   */
  @Input()
  public input : TypedDataHolder;

  @Input()
  public hints : StepPresentationHints;

  @Input()
  public default : any;

  @Input()
  public control : AbstractControl;

  @Input()
  public clearButton : boolean = true;

  @Input()
  public iconTray : boolean = true;

  @Input()
  public validationTray : boolean = true;

  @Input()
  public disableRestrictions : boolean = false;

  @Output()
  public customAnswer : EventEmitter<[string, Event]> =
      new EventEmitter<[string, Event]>();

  /**
   * A name to refer to the input
   * @type {string}
   */
  public name : string;

  constructor (
    protected controlBuilder : IntakeControlBuilder,
    protected settings : SettingService,
    protected hostElement : ElementRef,
    protected collaboration : CollaborationService,
    protected intakes : IntakeStepService,
    protected algorithms : AlgorithmService
  ) {}

  /**
   * Prepare the control for display and read & validate its configuration.
   */
  public ngOnInit () : void
  {
    // Verify required attributes are set
    if (!this.input)
    {
      throw new Error("Typed-control must have a defined 'input' attribute");
    }

    if (!this.input.id && !this.input.name)
    {
      throw new Error("Input is expected to have either a name or an ID");
    }
    TypedData.applyDefaults(this.input);
    // Object.assign(this.input, this.hints);

    if (this.input.control)
    {
      this.control = this.input.control;
    }
    else
    {
      if (this.requiresControl(this.input.type) && !this.control)
      {
        throw new Error(
          `TypedControl must supply a control for type ${this.input.type}`);
      }
    }
  }

  public addSubfield () : void
  {
    const value : any = (this.input.type === "natural number") ? 1 : null;
    this.controlBuilder.extend(this, value);
  }

  public removeSubfield (i : number) : void
  {
    this.controlBuilder.remove(this, i);
    this.manualChange();
  }

  private manualChange () : void
  {
    (this.hostElement.nativeElement as HTMLElement).dispatchEvent(
      new Event("change", {bubbles: true})
    );
  }

  // private manualSetAndChange (value : any) : void
  // {
  //   if (this.input.name === "q-fcwi-ready-for-results")
  //   {
  //     this.cheaty_delayedSaveAction();
  //   }
  //   this.input.control.setValue(value);
  //   this.manualChange();
  // }

  // private cheaty_delayedSaveAction () : void
  // {
  //   setTimeout(() => 
  //     this.algorithms.running.save(true).subscribe(
  //       r => console.log("Saved FCWI", r)),
  //     500);
  // }

  /**
   * Whether a DataType requires an AbstractControl to be supplied
   * @param type the data type to look up
   */
  private requiresControl (type : DataType) : boolean
  {
    return !(type === "json" || type === "empty");
  }

  @HostListener("keydown.control.m", ["$event"])
  @HostListener("keydown.meta.m", ["$event"])
  public addReminderForDate (event : KeyboardEvent & {targetDate? : Date})
    : void
  {
    if (this.input.type !== "date")
    {
      return;
    }
    event.targetDate = this.control.value;
  }

  // @HostListener("keydown.control.s", ["$event"])
  // @HostListener("keydown.meta.s", ["$event"])
  // public submitValueBeforeSave () : void
  // {
  //   this.manualChange();
  // }

  public changeCustom (e : Event, name : string) : false
  {
    this.customAnswer.emit([name, e]);
    e.stopPropagation();
    return false;
  }

  public cheaty_fcwiHouseholdEnabled () : boolean
  {
    const householdStep : (IntakeStep | null) =
      this.intakes.findNamed("q-fcwi-estimated-earned-monthly-household");
    return !!householdStep && !!householdStep.isActive;
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/common/data/typed-control.component.ts